<template>
    <Transition name="mode-switch" mode="out-in">
        <Button
            v-if="isEditing"
            key="view"
            class="view-button"
            icon="pi pi-eye"
            label="View"
            @click="stopEditing()"
            v-tooltip.left="{
                value: 'Return to view mode.',
                showDelay: 500,
            }"
        />
        <Button
            v-else-if="!isViewOnly"
            key="edit"
            severity="secondary"
            icon="pi pi-pencil"
            :label="canEdit ? 'Edit' : `Syncing (${secondsLeft})`"
            :loading="!canEdit"
            @click="startEditing()"
            v-tooltip.left="{
                value: 'Start editing.',
                showDelay: 500,
            }"
        />
    </Transition>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";

import { useEditManager, useEditStore } from "@/composables/edit-manager";
import { syncChannel } from "@/lib/broadcast-channels";
import { INACTIVITY_TIMEOUT_MS } from "@/stores/sync-store";

const { canEdit, isEditing, isViewOnly } = useEditManager();
const { startEditing, stopEditing } = useEditStore();
const secondsLeft = ref(0);
const interval = ref<NodeJS.Timeout>();

/**
 * Starts the countdown.
 */
const startCountdown = () => {
    secondsLeft.value = INACTIVITY_TIMEOUT_MS / 1000 + 1;
    interval.value = setInterval(() => {
        if (secondsLeft.value > 0) {
            secondsLeft.value -= 1;
        } else {
            clearInterval(interval.value);
        }
    }, 1000);
};

/**
 * (Re-)start the countdown on each new queue.
 */
syncChannel.addEventListener("message", ({ data: { message } }) => {
    if (message === "queued") {
        clearInterval(interval.value);
        startCountdown();
    }
});

/**
 * Clear countdown if you can edit again.
 */
watch(
    () => canEdit.value,
    (newValue) => {
        if (newValue) {
            clearInterval(interval.value);
        }
    }
);
</script>

<style lang="scss" scoped>
.view-button {
    background-color: var(--stekz-primary-background);
    border-color: var(--stekz-primary-background);
    color: var(--stekz-primary-color);
}
.p-button {
    white-space: nowrap;
}

.mode-switch-enter-active,
.mode-switch-leave-active {
    transition:
        opacity 120ms ease,
        transform 120ms ease;
}
.mode-switch-enter-from {
    opacity: 0;
    transform: scale(0.9);
}
.mode-switch-leave-to {
    opacity: 0;
    transform: scale(0.9);
}
</style>
